import { z } from 'zod';

import { axiosRubyClient } from '@/util/axios/axiosClient';

// GRAVY-10898 — "Invite backups" from the Shift Detail panel.
//
// These are graphy's /api/admin/jobs/:job_id/backup_invites and
// /api/admin/backup_invites/switch, not dashboard_v2 endpoints, so they are not
// in the generated dashboard_v2 schemas. The schemas are hand-written to the
// controllers' contract (API::Admin::JobBackupInvitesController,
// API::Admin::BackupInvitesSwitchController) and parsed like every other call
// here, so contract drift fails loudly instead of rendering a wrong list.
//
// All of them are TENANT_ADMIN-role only: helpdesk and client admins get 403.

const backupCandidate = z
  .object({
    worker_id: z.number().int(),
    name: z.string().nullish(),
    tier: z.string().nullish(),
    distance_mi: z.number().nullish(),
  })
  .passthrough();

const backupStatus = z.enum([
  'invited',
  'on_standby',
  'hired',
  'declined',
  'expired',
]);

const backupRow = z
  .object({
    worker_id: z.number().int(),
    name: z.string().nullish(),
    status: backupStatus,
    invited_at: z.string().nullish(),
    standby_at: z.string().nullish(),
  })
  .passthrough();

const backupSetting = z
  .object({
    enabled: z.boolean(),
    source: z.enum(['job', 'order', 'client']),
    job: z.boolean().nullable(),
    order: z.boolean().nullable(),
    // Clients are opt-in: a missing client value is off.
    client: z
      .boolean()
      .nullish()
      .transform((v) => v === true),
  })
  .passthrough();

export const backupInvitesResponse = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        job_id: z.number().int(),
        switch_on: z.boolean(),
        can_invite: z.boolean(),
        blocked_reason: z
          .enum([
            'switch_off',
            'job_cancelled',
            'job_started',
            // The client, order or job has backups off (job ?? order ?? client).
            'off_for_job',
            'off_for_order',
            'off_for_client',
          ])
          .nullable(),
        // The per-level setting behind `off_for_*`. Absent from an API older
        // than the per-level setting; the modal then shows no job control.
        setting: backupSetting.nullish(),
        open_spots: z.number().int(),
        candidates: z.array(backupCandidate),
        backups: z.array(backupRow),
      })
      .passthrough(),
  })
  .passthrough();

export const sendBackupInvitesResponse = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        job_id: z.number().int(),
        invited_count: z.number().int(),
        invited: z.array(
          z
            .object({
              worker_id: z.number().int(),
              job_offer_id: z.number().int().nullish(),
              message_status: z.enum(['sent', 'held']),
              scheduled_for: z.string().nullish(),
            })
            .passthrough(),
        ),
        skipped: z.array(
          z
            .object({
              worker_id: z.number().int(),
              reason: z.string(),
              message_reason: z.string().nullish(),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  })
  .passthrough();

export const backupInvitesSwitchResponse = z
  .object({
    success: z.literal(true),
    data: z.object({ key: z.string(), enabled: z.boolean() }).passthrough(),
  })
  .passthrough();

export type BackupInvitesData = z.infer<typeof backupInvitesResponse>['data'];
export type BackupCandidate = z.infer<typeof backupCandidate>;
export type BackupRow = z.infer<typeof backupRow>;
export type BackupStatus = z.infer<typeof backupStatus>;
export type BackupBlockedReason = BackupInvitesData['blocked_reason'];
export type BackupSetting = z.infer<typeof backupSetting>;
export type SendBackupInvitesResult = z.infer<
  typeof sendBackupInvitesResponse
>['data'];
export type BackupInvitesSwitch = z.infer<
  typeof backupInvitesSwitchResponse
>['data'];

export const backupInvitesClient = {
  async getBackupInvites(jobId: number): Promise<BackupInvitesData> {
    const { data } = await axiosRubyClient.get(
      `/api/admin/jobs/${jobId}/backup_invites`,
    );
    return backupInvitesResponse.parse(data).data;
  },

  // Always sends the explicit list the admin ticked. The endpoint reads a
  // MISSING `worker_ids` as "every candidate", so omitting it here would invite
  // workers the admin unticked.
  async sendBackupInvites(
    jobId: number,
    workerIds: number[],
  ): Promise<SendBackupInvitesResult> {
    const { data } = await axiosRubyClient.post(
      `/api/admin/jobs/${jobId}/backup_invites`,
      { worker_ids: workerIds },
    );
    return sendBackupInvitesResponse.parse(data).data;
  },

  async getSwitch(): Promise<BackupInvitesSwitch> {
    const { data } = await axiosRubyClient.get(
      '/api/admin/backup_invites/switch',
    );
    return backupInvitesSwitchResponse.parse(data).data;
  },

  // GLOBAL: automated backup invites, standby and auto-hire from standby for
  // every shift, not just the one in the panel.
  async setSwitch(enabled: boolean): Promise<BackupInvitesSwitch> {
    const { data } = await axiosRubyClient.patch(
      '/api/admin/backup_invites/switch',
      { enabled },
    );
    return backupInvitesSwitchResponse.parse(data).data;
  },
};
